import type { RegraDeParaIBSCBSApi, RegraIbsCbsEntradaApi } from '@/services/Configuracoes';

export interface LinhaRegraIbsCbs {
  id: number;
  classTrib: string;
  cst: string;
  descricao: string;
  regra: string;
  nomeRegra: string | null;
  vigencia: string;
  reducaoIbs: string;
  reducaoCbs: string;
  creditoIbs: string;
  creditoCbs: string;
  regimeRegular: string;
  usos: string;
  tratamento: string;
  origem: string;
  /** Quem mudou e quando; para a linha do padrão, a versão dele. */
  detalheDaOrigem: string;
  versaoNovaDoPadrao: boolean;
  /** Os três primeiros dígitos da classificação; vazio quando ela não tem seis dígitos. */
  cstEsperado: string;
  /** "CST diferente da classificação" (SPEC-008 §3 item 1a). */
  cstDivergente: boolean;
  api: RegraDeParaIBSCBSApi;
}

/**
 * O CST que a classificação manda e se a linha diverge dele. A API manda os dois campos; enquanto a rota
 * de regras puder ser servida pelo legado (que não os conhece), a tela faz a mesma conta — é a mesma regra:
 * os três primeiros dígitos da classificação de seis dígitos.
 */
export function cstDaClassificacao(linha: RegraDeParaIBSCBSApi): {
  esperado: string;
  divergente: boolean;
} {
  const classTrib = (linha.ClassTrib ?? '').trim();
  const esperado = linha.CstEsperado ?? (/^\d{6}$/.test(classTrib) ? classTrib.slice(0, 3) : '');
  const cst = (linha.CSTIbsCbs ?? '').trim();
  const divergente =
    linha.CstDiferenteDaClassificacao ?? (esperado !== '' && cst !== '' && cst !== esperado);
  return { esperado: esperado ?? '', divergente };
}

const NOMES_DOS_USOS: Record<string, string> = {
  '1': 'revenda',
  '2': 'insumo',
  '3': 'uso e consumo',
  '4': 'ativo imobilizado',
  '5': 'outros',
  '6': 'formulação direta',
  '7': 'uso e consumo sem crédito de PIS/Cofins',
};

/** O nome de um uso só, o de um item da nota (SPEC-018, §1, decisão 5): sem uso, "não informado". */
export function rotuloUso(uso: number | string | null | undefined): string {
  const codigo = uso === null || uso === undefined ? '' : String(uso).trim();
  if (codigo === '') return 'não informado';
  return NOMES_DOS_USOS[codigo] ?? codigo;
}

export function rotuloUsos(usos: string | null | undefined): string {
  if (usos === null || usos === undefined) return 'qualquer';
  if (usos.trim() === '') return 'nenhum';
  return usos
    .split(',')
    .map(codigo => NOMES_DOS_USOS[codigo.trim()] ?? codigo.trim())
    .join(', ');
}

/** Espelha o tratamento que a API aplica: crédito de regra não validada, ou sem a regra importada, fica pendente. */
export function rotuloCredito(
  codigo: string | null | undefined,
  regra: RegraIbsCbsEntradaApi | undefined
): string {
  const valor = (codigo ?? '').trim().toUpperCase();
  const concedeCredito = valor === 'S' || valor === 'P';

  if (concedeCredito && !regra) return 'Pendente (regra não importada)';
  if (concedeCredito && !regra?.ValidadaPelaPO) return 'Pendente (validação da P.O.)';

  switch (valor) {
    case 'S':
      return 'Com crédito';
    case 'N':
      return 'Sem crédito';
    case 'P':
      return 'Presumido';
    case 'X':
      return 'Ajuste';
    default:
      return 'Pendente';
  }
}

export function formatarData(iso: string | null | undefined): string {
  if (!iso) return '';
  const [ano, mes, dia] = iso.slice(0, 10).split('-');
  return dia && mes && ano ? `${dia}/${mes}/${ano}` : '';
}

function formatarVigencia(inicio: string | null | undefined, fim: string | null | undefined) {
  const de = formatarData(inicio);
  const ate = formatarData(fim);
  return ate ? `${de} a ${ate}` : `desde ${de}`;
}

function formatarPercentual(valor: number | null | undefined) {
  return `${(valor ?? 0).toLocaleString('pt-BR', { maximumFractionDigits: 4 })}%`;
}

export function montarLinhasRegrasIbsCbs(
  classificacoes: RegraDeParaIBSCBSApi[],
  regras: RegraIbsCbsEntradaApi[]
): LinhaRegraIbsCbs[] {
  const porRegra = new Map(regras.map(regra => [regra.Regra, regra]));

  return classificacoes
    .filter(linha => !!linha.ClassTrib)
    .map(linha => {
      const regra = porRegra.get((linha.Regra ?? '').slice(0, 3));
      return {
        id: linha.ID,
        classTrib: linha.ClassTrib ?? '',
        cst: linha.CSTIbsCbs ?? '',
        descricao: linha.Descricao ?? '',
        regra: linha.Regra ?? '',
        nomeRegra: regra?.NomeRegra ?? null,
        vigencia: formatarVigencia(linha.VigenciaInicio, linha.VigenciaFim),
        reducaoIbs: formatarPercentual(linha.ReducaoIBS),
        reducaoCbs: formatarPercentual(linha.ReducaoCBS),
        creditoIbs: rotuloCredito(linha.RF_TomarCreditoIBS, regra),
        creditoCbs: rotuloCredito(linha.RF_TomarCreditoCBS, regra),
        regimeRegular: regra ? (regra.RegimeRegularExigido ? 'exigido' : 'não exigido') : 'exigido',
        usos: regra ? rotuloUsos(regra.UsosPermitidos) : 'qualquer',
        tratamento: linha.TratamentoNaEntrada ?? '',
        origem: rotuloOrigem(linha.Origem),
        detalheDaOrigem: detalheDaOrigem(linha),
        versaoNovaDoPadrao: !!linha.HaVersaoNovaDoPadrao,
        cstEsperado: cstDaClassificacao(linha).esperado,
        cstDivergente: cstDaClassificacao(linha).divergente,
        api: linha,
      };
    });
}

export function filtrarLinhasRegrasIbsCbs(linhas: LinhaRegraIbsCbs[], termo: string) {
  const busca = termo.trim().toLowerCase();
  if (!busca) return linhas;
  return linhas.filter(linha =>
    [linha.classTrib, linha.cst, linha.regra, linha.descricao, linha.nomeRegra ?? '']
      .join(' ')
      .toLowerCase()
      .includes(busca)
  );
}

/** O que falta importar para a regra valer inteira; nulo quando os dois arquivos estão lá. */
export function faltaImportar(classificacoes: number, regras: number): string | null {
  if (classificacoes === 0 && regras === 0) {
    return 'Importe os dois arquivos do De/Para: cclasstrib-tratamento-na-entrada.csv e depara-ibs-cbs-entrada.csv.';
  }
  if (classificacoes === 0) {
    return 'Falta importar cclasstrib-tratamento-na-entrada.csv: sem ele nenhum item tem regra.';
  }
  if (regras === 0) {
    return 'Falta importar depara-ibs-cbs-entrada.csv: sem ele todo crédito fica pendente.';
  }
  return null;
}

const ROTULOS_DA_ORIGEM: Record<string, string> = {
  padrao: 'Padrão',
  alterada: 'Alterada pela empresa',
  incluida: 'Incluída pela empresa',
};

export function rotuloOrigem(origem: string | null | undefined): string {
  return (origem && ROTULOS_DA_ORIGEM[origem]) ?? '—';
}

export function detalheDaOrigem(linha: RegraDeParaIBSCBSApi): string {
  if (linha.Origem === 'padrao') {
    return linha.VersaoDoPadrao ? `versão de ${formatarData(linha.VersaoDoPadrao)}` : '';
  }
  const quando = formatarData(linha.DataHoraManutencao);
  return [linha.UsuarioManutencao, quando].filter(Boolean).join(' em ');
}

export const CODIGOS_DE_CREDITO = ['S', 'N', 'P', 'X', 'pendente'] as const;

/** O formulário de incluir ou alterar uma classificação, como texto, do jeito que a tela o edita. */
export interface FormularioClassificacao {
  classTrib: string;
  cst: string;
  descricao: string;
  regra: string;
  vigenciaInicio: string;
  vigenciaFim: string;
  reducaoIbs: string;
  reducaoCbs: string;
  creditoIbs: string;
  creditoCbs: string;
  tratamento: string;
}

export const FORMULARIO_VAZIO: FormularioClassificacao = {
  classTrib: '',
  cst: '',
  descricao: '',
  regra: '',
  vigenciaInicio: '',
  vigenciaFim: '',
  reducaoIbs: '0',
  reducaoCbs: '0',
  creditoIbs: 'pendente',
  creditoCbs: 'pendente',
  tratamento: '',
};

function percentualEditavel(valor: number | null | undefined) {
  return (valor ?? 0).toLocaleString('pt-BR', { maximumFractionDigits: 4, useGrouping: false });
}

export function formularioDaLinha(linha: RegraDeParaIBSCBSApi): FormularioClassificacao {
  return {
    classTrib: linha.ClassTrib ?? '',
    cst: linha.CSTIbsCbs ?? '',
    descricao: linha.Descricao ?? '',
    regra: linha.Regra ?? '',
    vigenciaInicio: (linha.VigenciaInicio ?? '').slice(0, 10),
    vigenciaFim: (linha.VigenciaFim ?? '').slice(0, 10),
    reducaoIbs: percentualEditavel(linha.ReducaoIBS),
    reducaoCbs: percentualEditavel(linha.ReducaoCBS),
    creditoIbs: linha.RF_TomarCreditoIBS ?? 'pendente',
    creditoCbs: linha.RF_TomarCreditoCBS ?? 'pendente',
    tratamento: linha.TratamentoNaEntrada ?? '',
  };
}

function lerPercentual(texto: string): number | null {
  const limpo = texto.replace('%', '').replace(',', '.').trim();
  if (limpo === '') return 0;
  const valor = Number(limpo);
  return Number.isFinite(valor) ? valor : null;
}

/** O que a tela recusa antes de mandar; o servidor confere de novo (os mesmos formatos da importação). */
export function erroDoFormulario(form: FormularioClassificacao): string | null {
  if (!/^\d{6}$/.test(form.classTrib.trim())) return 'O cClassTrib tem seis dígitos.';
  if (form.cst.trim() !== form.classTrib.trim().slice(0, 3)) {
    return `O CST de IBS/CBS do cClassTrib ${form.classTrib.trim()} é ${form.classTrib.trim().slice(0, 3)}.`;
  }
  if (!/^R\d{2}(\/R\d{2})?$/i.test(form.regra.trim())) {
    return 'Informe a regra (R01 a R22, ou duas separadas por barra).';
  }
  if (!form.vigenciaInicio) return 'Informe o início da vigência.';
  if (form.vigenciaFim && form.vigenciaFim < form.vigenciaInicio) {
    return 'O fim da vigência é anterior ao início.';
  }
  for (const texto of [form.reducaoIbs, form.reducaoCbs]) {
    const valor = lerPercentual(texto);
    if (valor === null || valor < 0 || valor > 100) return 'A redução vai de 0 a 100%.';
  }
  return null;
}

/** O corpo do POST: a linha original (para manter o ID e o que a tela não edita) com o que o formulário mudou. */
export function paraSalvar(
  form: FormularioClassificacao,
  original?: RegraDeParaIBSCBSApi
): Partial<RegraDeParaIBSCBSApi> {
  return {
    ...(original ?? {}),
    ID: original?.ID ?? 0,
    ClassTrib: form.classTrib.trim(),
    CSTIbsCbs: form.cst.trim(),
    CSTOrigem: form.cst.trim(),
    Descricao: form.descricao.trim(),
    Regra: form.regra.trim().toUpperCase(),
    VigenciaInicio: form.vigenciaInicio,
    VigenciaFim: form.vigenciaFim || null,
    ReducaoIBS: lerPercentual(form.reducaoIbs) ?? 0,
    ReducaoCBS: lerPercentual(form.reducaoCbs) ?? 0,
    RF_TomarCreditoIBS: form.creditoIbs,
    RF_TomarCreditoCBS: form.creditoCbs,
    TratamentoNaEntrada: form.tratamento.trim() || null,
  };
}
